import React from 'react';
import { UserReward } from '../Types/UserReward';

export enum RewardCardRarity {
  Common = 'common',
  Rare = 'rare',
  Epic = 'epic',
  Legendary = 'legendary',
}

export interface RewardCardProps {
  reward: UserReward;
  title: string;
  description?: string;
  rarity?: RewardCardRarity;
  content: React.ReactNode;
  children?: React.ReactNode;
  type?: string;
}

const RarityConfig = {
  [RewardCardRarity.Common]: {
    borderColor: 'border-gray-400',
    textColor: 'text-gray-700',
    label: 'Common',
  },
  [RewardCardRarity.Rare]: {
    borderColor: 'border-blue-500',
    textColor: 'text-blue-700',
    label: 'Rare',
  },
  [RewardCardRarity.Epic]: {
    borderColor: 'border-purple-500',
    textColor: 'text-purple-700',
    label: 'Epic',
  },
  [RewardCardRarity.Legendary]: {
    borderColor: 'border-yellow-500',
    textColor: 'text-yellow-700',
    label: 'Legendary',
  },
};

// Shared RewardCard component for consistent styling
const RewardCard: React.FC<RewardCardProps> = ({
  title,
  description,
  rarity = RewardCardRarity.Common,
  content,
  children,
  type,
}) => {
  return (
    <div
      className={`relative w-[140px] h-[200px] rounded-lg overflow-hidden shadow-lg transition-all duration-200 bg-white border-2 ${RarityConfig[rarity].borderColor} ${RarityConfig[rarity].textColor}`}
    >
      {/* Header with rarity and type labels */}
      <div className="flex justify-between items-start pt-1 px-1">
        <div
          className={`text-[8px] font-bold px-1.5 py-0.5 rounded-sm bg-white ${RarityConfig[rarity].borderColor} border border-current`}
        >
          {RarityConfig[rarity].label}
        </div>

        {type && (
          <div className="text-[8px] bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded-sm">
            {type}
          </div>
        )}
      </div>

      {/* Card content area */}
      <div className="absolute inset-0 pt-8 pb-3 px-2 flex flex-col items-center">
        {/* Card content (passed in from parent) */}
        {content}

        {/* Title */}
        <h3
          className="font-bold text-center text-gray-800 mt-1 px-1 text-sm"
          title={title}
        >
          {title}
        </h3>

        {/* Description */}
        {description && (
          <p
            className="text-xs text-center text-gray-600 mt-1 overflow-hidden px-1"
            title={description}
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {description}
          </p>
        )}

        {children}
      </div>
    </div>
  );
};

export default RewardCard;
